define('webshopProductsCategoriesView',[
  'webshopProductsCategoriesI18n',
  'app',
  'module',
  'backbone',
  'marionette',
  'underscore',
  'buttonView',
  'modalsConfirmationView',
  'modalsAlertView',
  'webshopCategoriesFinderView',
  'webshopProductsCategoriesClipboardView',
  'webshopProductsCategoriesListView',
  'entities/webshop/categories',
  'template!webshopProductsCategoriesView'
], function (
  translate,
  App,
  Module,
  Backbone,
  Marionette,
  _,
  ButtonView,
  ConfirmationView,
  AlertView,
  CategoriesFinderView,
  ClipboardView,
  ListView,
  Categories,
  Tpl
) {
  'use strict';

  /**
   * Products x categories grid.
   *
   * Each product row shows one cell per category it belongs to. Clicking a
   * filled cell copies its category (the "clipboard" shown in the header),
   * an empty cell can receive a new category (finder) or the copied one.
   * The header buttons paste / remove the copied category on every selected row.
   */
  Module.exports = Marionette.LayoutView.extend({
    template: Tpl,
    className: 'webshop-products-categories-view',

    ui: {
      clipboardEmpty: '.header .clipboard-empty',
      selectionInfos: '.header .selection-infos'
    },

    regions: {
      clipboardRegion: '.header .clipboard-region',
      chooseRegion: '.header .choose-region',
      pasteRegion: '.header .paste-region',
      removeRegion: '.header .remove-region',
      listRegion: '.list-region'
    },

    initialize: function () {
      // the copied category: {id, code, name}, id === '' when empty
      this.clipboard = new Backbone.Model({id: '', code: '', name: ''});
      this.selectedProducts = new Backbone.Model({products: []});
      this.running = false;
    },

    serializeData: function () {
      return {
        clipboardLabel: translate('webshop.products.categories.clipboard', 'Copied category'),
        clipboardEmpty: translate('webshop.products.categories.clipboard.empty', 'none - click a category in the list, or choose one')
      };
    },

    onShow: function () {
      this.clipboardView = new ClipboardView({
        model: this.clipboard
      });
      this.getRegion('clipboardRegion').show(this.clipboardView);

      //----------------
      this.chooseButtonView = new ButtonView({
        type: 'find',
        color: 'lightgrey',
        text: translate('webshop.products.categories.clipboard.choose', 'Choose a category'),
        title: translate('webshop.products.categories.clipboard.choose.title', 'Choose the category to paste')
      });
      this.getRegion('chooseRegion').show(this.chooseButtonView);
      this.chooseButtonView.on('click', _.bind(this.onChoose, this));

      //----------------
      this.pasteButtonView = new ButtonView({
        type: 'add',
        color: 'orange',
        text: translate('webshop.products.categories.paste', 'Paste on selection'),
        title: translate('webshop.products.categories.paste.title', 'Add the copied category to the selected products')
      });
      this.getRegion('pasteRegion').show(this.pasteButtonView);
      this.pasteButtonView.on('click', _.bind(this.onPaste, this));

      //----------------
      this.removeButtonView = new ButtonView({
        type: 'remove',
        color: 'grey',
        text: translate('webshop.products.categories.remove', 'Remove from selection'),
        title: translate('webshop.products.categories.remove.title', 'Remove the copied category from the selected products')
      });
      this.getRegion('removeRegion').show(this.removeButtonView);
      this.removeButtonView.on('click', _.bind(this.onRemove, this));

      //----------------
      this.listView = new ListView({
        selectedProducts: this.selectedProducts,
        clipboard: this.clipboard
      });
      this.getRegion('listRegion').show(this.listView);

      //----------------
      this.listenTo(this.clipboard, 'change', this.renderState);
      this.listenTo(this.selectedProducts, 'change', this.renderState);
      this.renderState();
    },

    renderState: function () {
      if (this.isDestroyed)
        return;

      var products = this.selectedProducts.get('products');
      var hasClipboard = this.clipboard.get('id') !== '';

      if (hasClipboard)
        this.ui.clipboardEmpty.hide();
      else
        this.ui.clipboardEmpty.css('display', 'flex');

      this.ui.selectionInfos.html(
        translate('webshop.products.categories.selection', '%s product(s) selected').replace('%s', products.length));

      var enabled = !this.running && products.length > 0 && hasClipboard;
      this.pasteButtonView.enabled(enabled);
      this.removeButtonView.enabled(enabled);
      this.chooseButtonView.enabled(!this.running);
    },

    onChoose: function () {
      CategoriesFinderView.prototype.open({
        title: translate('webshop.products.categories.clipboard.finder.title', 'Choose the category to paste'),
        onSelected: _.bind(function (categories) {
          if (categories.length > 0) {
            this.clipboard.set({
              id: categories[0].get('id'),
              code: categories[0].get('code'),
              name: categories[0].getName ? categories[0].getName() : categories[0].get('code')
            });
          }
        }, this)
      });
    },

    onPaste: function () {
      this.run('add');
    },

    onRemove: function () {
      var products = this.selectedProducts.get('products');

      ConfirmationView.prototype.open({
        title: translate('webshop.products.categories.remove.confirmation.title', 'Remove from category'),
        text: translate('webshop.products.categories.remove.confirmation.text', 'Remove %s product(s) from the category "%c"?')
          .replace('%s', products.length)
          .replace('%c', this.clipboard.get('code')),
        onConfirm: _.bind(function () {
          this.run('remove');
        }, this)
      });
    },

    /**
     * Adds / removes the copied category on every selected product.
     */
    run: function (action) {
      if (this.running)
        return;

      var productIds = _.map(this.selectedProducts.get('products'), function (product) {
        return product.getId();
      });
      var categoryId = this.clipboard.get('id');

      if (productIds.length === 0 || categoryId === '')
        return;

      this.running = true;
      this.renderState();
      var button = action === 'add' ? this.pasteButtonView : this.removeButtonView;
      button.loading(true);

      var request;
      if (action === 'add')
        request = App.request('webshop.categories.products.add', categoryId, productIds);
      else
        request = App.request('webshop.categories.products.delete', categoryId, productIds);

      request.done(_.bind(function () {
        this.onRunDone(button);
      }, this)).fail(_.bind(function (message) {
        this.onRunDone(button);
        AlertView.prototype.open(
          translate('webshop.products.categories.error.title', 'Error'),
          message || translate('webshop.products.categories.error.text', 'The update failed'));
      }, this));
    },

    onRunDone: function (button) {
      this.running = false;

      if (this.isDestroyed)
        return;

      button.loading(false);
      this.renderState();
      this.listView.refreshCategories();
    }
  });
});

